/**
 * PosNumberPad — a big touch keypad for cash-tendered entry, ported from the
 * Tally design (components/forms/NumberPad). Works in CENTS like a card
 * terminal: each digit pushes value*10+d, so "1000" = €10.00. Presets (€10 /
 * €20 / €50 / Exact) fast-fill common tenders. Purely a controlled value — the
 * caller owns `tendered` and renders the change readout. Styling lives in
 * styles/pos-theme/pos-screen.css (.pos-numpad*).
 */
const eur = (cents: number): string =>
  (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export interface PosNumberPadProps {
  value: number;
  onChange: (cents: number) => void;
  presets?: number[];
  exactCents?: number | null;
  showPresets?: boolean;
}

export const PosNumberPad = ({
  value,
  onChange,
  presets = [1000, 2000, 5000],
  exactCents = null,
  showPresets = true,
}: PosNumberPadProps) => {
  const push = (d: number) => onChange(Math.min(value * 10 + d, 99999999));
  const back = () => onChange(Math.floor(value / 10));
  const hasExact = exactCents != null && exactCents > 0;
  return (
    <div className="pos-numpad">
      <div className="pos-numpad__display">
        <span className="pos-numpad__cur">€</span>
        <span className="pos-numpad__amt">{eur(value)}</span>
      </div>
      {showPresets && (
        <div className="pos-numpad__presets">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              className="pos-numpad__preset"
              onClick={() => onChange(p)}
            >
              €{Math.round(p / 100)}
            </button>
          ))}
        </div>
      )}
      {/* Big keypad on the left + a tall full-height EXACT key on the right. */}
      <div className="pos-numpad__body">
        <div className="pos-numpad__grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              type="button"
              className="pos-numpad__key"
              onClick={() => push(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className="pos-numpad__key pos-numpad__key--wide"
            onClick={() => onChange(0)}
          >
            C
          </button>
          <button
            type="button"
            className="pos-numpad__key"
            onClick={() => push(0)}
          >
            0
          </button>
          <button
            type="button"
            className="pos-numpad__key"
            aria-label="Backspace"
            onClick={back}
          >
            ⌫
          </button>
        </div>
        {hasExact && (
          <button
            type="button"
            className="pos-numpad__exact"
            onClick={() => onChange(exactCents)}
          >
            <span className="pos-numpad__exact-label">Exact</span>
            <span className="pos-numpad__exact-amt">€{eur(exactCents)}</span>
          </button>
        )}
      </div>
    </div>
  );
};
